import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import styled from "styled-components";
import Api from "api/Api";
import {selectCompanyInfo} from "store/companySlice";
import MainContainer from "layout/atoms/MainContainer";

const Container = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    padding: 0 0 80px;
  }
`;

const Header = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: var(--black);
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: var(--gray);
  margin-bottom: 40px;
`;

const FormCard = styled.div`
  background: var(--content-box-bg);
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 32px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--black);
  margin-bottom: 8px;
`;

const ContactsCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f6f8fa;
  border-radius: 6px;
  border: 1px solid #d0d7de;
`;

const CounterIcon = styled.div`
  width: 40px;
  height: 40px;
  background: var(--brand-ideolo);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: 600;
`;

const CounterInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CounterNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--black);
  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
  transition: opacity 0.2s ease;
`;

const CounterLabel = styled.div`
  font-size: 12px;
  color: var(--gray);
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  box-sizing: border-box;
  padding: 12px 16px;
  font-size: 14px;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: var(--black);
  background: var(--white);
  border: 1px solid #d0d7de;
  border-radius: 6px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-ideolo);
    box-shadow: 0 0 0 3px rgba(83, 210, 61, 0.1);
  }

  &::placeholder {
    color: #6e7681;
  }
`;

const CharCount = styled.div`
  font-size: 12px;
  color: ${({ exceeded }) => (exceeded ? "#dd2b0e" : "#6e7681")};
  text-align: right;
  margin-top: 6px;
`;

const TagsSelection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  min-height: 48px;
  background: var(--white);
`;

const TagCheckbox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: ${({ checked }) => (checked ? "#ddf4ff" : "#f6f8fa")};
  border: 1px solid ${({ checked }) => (checked ? "#0969da" : "#d0d7de")};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ checked }) => (checked ? "#0969da" : "var(--black)")};
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: ${({ checked }) => (checked ? "#b6e3ff" : "#e1e4e8")};
  }

  input {
    margin: 0;
    cursor: pointer;
  }
`;

const EmptyTags = styled.div`
  color: var(--gray);
  font-size: 14px;
  text-align: center;
  padding: 12px;
`;

const ErrorMessage = styled.div`
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  padding: 12px 16px;
  color: #dd2b0e;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 8px;

    button {
      width: 100%;
    }
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &:active {
    transform: translateY(1px);
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: var(--gray);
  border: 1px solid #d0d7de;

  &:hover:not(:disabled) {
    background: #f6f8fa;
    border-color: #8c959f;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SaveButton = styled(Button)`
  background: var(--brand-ideolo);
  color: white;

  &:hover {
    background: #4bc235;
    box-shadow: 0 2px 8px rgba(83, 210, 61, 0.3);
  }

  &:disabled {
    background: #d0d7de;
    cursor: not-allowed;
    transform: none;
  }
`;

const SMS_MAX_LENGTH = 1500;

function NewSmsJob() {
  const navigate = useNavigate();
  const { t } = useTranslation("translation", {
    keyPrefix: "messaging.newJob",
  });
  const api = new Api();
  const companyInfo = useSelector(selectCompanyInfo);

  const [companyId, setCompanyId] = useState(companyInfo.id);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [contactsCount, setContactsCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Company info is loaded asynchronously; wait until it's available
      const currentCompanyId = companyInfo.id;
      if (!currentCompanyId) {
        return;
      }

      try {
        setCompanyId(currentCompanyId);

        // Fetch available tags
        const tags = await api.getContactTags(currentCompanyId);
        setAvailableTags(tags);

        // Fetch initial recipients count (all contacts)
        const countResponse = await api.getSmsRecipientsCount(currentCompanyId, []);
        setContactsCount(countResponse.count);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError(t("errors.failedToLoad"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo.id]);

  // Update recipients count when selected tags change
  useEffect(() => {
    if (!companyId) return;

    const fetchRecipientsCount = async () => {
      setLoadingCount(true);
      try {
        const countResponse = await api.getSmsRecipientsCount(companyId, selectedTags);
        setContactsCount(countResponse.count);
      } catch (err) {
        console.error("Failed to fetch recipients count:", err);
      } finally {
        setLoadingCount(false);
      }
    };

    fetchRecipientsCount();
  }, [selectedTags, companyId]);

  const handleToggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag)
        ? prev.filter((t) => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSave = async () => {
    if (!companyId) {
      setError(t("errors.companyNotLoaded"));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Create the job with the API structure
      const newJob = await api.createSmsJob(companyId, {
        audience: {
          byTags: selectedTags, // Empty array means all contacts
        },
        message: message,
      });

      // Navigate back to SMS list with the new job in state
      navigate("/admin/messaging/sms", {
        state: { newJob }
      });
    } catch (err) {
      console.error("Failed to create SMS job:", err);
      setError(t("errors.failedToCreate"));
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/messaging/sms");
  };

  const isMessageTooLong = message.length > SMS_MAX_LENGTH;

  if (loading) {
    return (
      <MainContainer>
        <Container>
          <div>{t("loading")}</div>
        </Container>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
    <Container>
      <Header>{t("header")}</Header>
      <Description>{t("description")}</Description>

      <FormCard>
        <FormGroup>
          <Label>{t("contactsLabel")}</Label>
          <ContactsCounter>
            <CounterIcon>👥</CounterIcon>
            <CounterInfo>
              <CounterNumber loading={loadingCount}>
                {contactsCount.toLocaleString()}
              </CounterNumber>
              <CounterLabel>{t("contactsDescription")}</CounterLabel>
            </CounterInfo>
          </ContactsCounter>
        </FormGroup>

        <FormGroup>
          <Label>
            {t("filterByTags")} {selectedTags.length > 0 && `(${selectedTags.length} ${t("selected")})`}
          </Label>
          <TagsSelection>
            {availableTags.length === 0 ? (
              <EmptyTags>{t("noTagsAvailable")}</EmptyTags>
            ) : (
              availableTags.map((tag) => (
                <TagCheckbox key={tag} checked={selectedTags.includes(tag)}>
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag)}
                    onChange={() => handleToggleTag(tag)}
                  />
                  {tag}
                </TagCheckbox>
              ))
            )}
          </TagsSelection>
        </FormGroup>

        <FormGroup>
          <Label>{t("messageLabel")}</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("messagePlaceholder")}
          />
          <CharCount exceeded={isMessageTooLong}>
            {message.length} / {SMS_MAX_LENGTH} {t("characters")}
            {isMessageTooLong &&
              ` (${message.length - SMS_MAX_LENGTH} ${t("overLimit")})`}
          </CharCount>
        </FormGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <CancelButton onClick={handleCancel} disabled={submitting}>
            {t("cancel")}
          </CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={!message || isMessageTooLong || submitting}
          >
            {submitting ? t("creating") : t("save")}
          </SaveButton>
        </ButtonGroup>
      </FormCard>
    </Container>
    </MainContainer>
  );
}

export default NewSmsJob;
